import { ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from "@angular/core";
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { IRadioButton } from "@app/directives/_forms/_radio-buttons/radio-button-base/radio-button-base.component";
import { CustomFormValidators } from "@app/directives/_forms/form-validation.service";
import { NavigationService } from "@app/services/navigation.service";
import { TranslationService } from "@app/services/translation.service";
import { copy, StateParams } from "@uirouter/angularjs";
import { ResourceManagementNavigationModule } from "../../resourceManagement.navigation";
import { DrivingStyleMetricEditorComponent } from "../driving-style-metric-editor/driving-style-metric-editor.component";
import { DrivingStyleMetricSelectorComponent } from "../driving-style-metric-selector/driving-style-metric-selector.component";
import { DrivingStyleMetric, DrivingStyleMetricCategory, DrivingStyleType, DrivingStyleTypeDescription, DrivingStyleWeightingType, MetricMappingWeightingLevel } from "../driving-styles.enums";
import { IDrivingPerformanceSummary, IDrivingStyle, IDrivingStyleMetricDescriptionDetails, IDrivingStyleMetricMapping, IDrivingStyleMetricMetadataItem } from "../driving-styles.interfaces";
import { DrivingStylesService, IMetricMappingWeightingLevelDetail } from "../driving-styles.service";
import './driving-style-editor.component.scss';

@Component({
    selector: 'driving-style-editor',
    template: require('./driving-style-editor.component.html')
})
export class DrivingStyleEditorComponent implements OnInit, OnChanges {

    @Input() drivingStyleId: number = -1;
    @Input() drivingStyle: IDrivingStyle;

    dirtyDrivingStyle: IDrivingStyle;

    allDefaultDrivingStyles: IDrivingStyle[];
    defaultDrivingStyle: IDrivingStyle;
    drivingStyles: IDrivingStyle[];
    drivingStyleMetricDescriptionDetails: IDrivingStyleMetricDescriptionDetails[];

    loading: boolean;
    saving: boolean;
    deleting: boolean;

    enableWeighting: boolean;
    associationsValid: boolean;

    @ViewChild(DrivingStyleMetricSelectorComponent) drivingStyleMetricSelector: DrivingStyleMetricSelectorComponent;
    @ViewChild(DrivingStyleMetricEditorComponent) drivingStyleMetricEditor: DrivingStyleMetricEditorComponent;

    selectedMetricMapping: IDrivingStyleMetricMapping;
    originalSelectedMetricMapping: IDrivingStyleMetricMapping;

    metricMappingWeightingLevelDetails: IMetricMappingWeightingLevelDetail[];

    get selectedMetricMappingTitle(): string {
        return (this.selectedMetricMapping)
            ? `${this.selectedMetricMapping.drivingStyleMetricName} metric configuration`
            : null;
    }

    get disableSaveStyle(): boolean {
        return !this.drivingStyleForm?.valid
            || !this.dirtyDrivingStyle?.DrivingStyleMetricMappings?.length
            || !!this.dirtyDrivingStyle?.metricMappingsError
            || !this.associationsValid
    }

    get isScoringValid(): boolean {
        return this.drivingStyleMetricEditor?.metricForm.controls.VirtualHighScoreValue.valid;
    }

    availableDrivingStyleMetricMappings: IDrivingStyleMetricMapping[];

    DrivingStyleMetricCategory = DrivingStyleMetricCategory;
    DrivingStyleWeightingType = DrivingStyleWeightingType;

    drivingStyleForm: FormGroup;

    selectedType: DrivingStyleType;

    radioButtons: IRadioButton[] =[];
    selectedRadioButton: IRadioButton;

    readonly maxTotalAllowedMetrics: number = 8;


    get upperRagBoundControl(): AbstractControl {
        return this.drivingStyleForm?.get("UpperRagBound");
    }

    get lowerRagBoundControl(): AbstractControl {
        return this.drivingStyleForm?.get("LowerRagBound");
    }

    get isNew(): boolean {
        return this.drivingStyleId === -1;
    }

    constructor(
        private readonly cd: ChangeDetectorRef,
        private readonly formBuilder: FormBuilder,
        private readonly $stateParams: StateParams,
        private readonly drivingStylesService: DrivingStylesService,
        private readonly navigationService: NavigationService,
        private readonly translationService: TranslationService
    ) {
    }

    ngOnInit(): void {
        this.applyRouterDrivingStyleId();

        this.getData();
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.applyRouterDrivingStyleId();
    }

    async save(): Promise<void> {
        this.saving = true;

        this.setStyleIdForMetricMappings(this.dirtyDrivingStyle);
        this.fixAssociatons(this.dirtyDrivingStyle);
        this.setType();

        const drivingStyleId: number = await this.drivingStylesService.saveDrivingStyle(this.dirtyDrivingStyle);

        if (drivingStyleId < 1) {
            this.saving = false;
            console.error("Failed to save driving style");
            return;
        }

        setTimeout(() => {
            this.saving = false;
            this.navigationService.go(ResourceManagementNavigationModule.DrivingStylesManagement);
        }, 1000);
    }

    async delete(): Promise<void> {
        this.deleting = true;

        await this.drivingStylesService.deleteDrivingStyleById(this.dirtyDrivingStyle.Id);

        setTimeout(() => {
            this.deleting = false;
            this.navigationService.go(ResourceManagementNavigationModule.DrivingStylesManagement);
        }, 1000);
    }

    onConfirmDeleteButton(confirmed: boolean): void {
        if (!confirmed) {
            return;
        }

        this.delete();
    }

    close(): void {
        this.navigationService.go(ResourceManagementNavigationModule.DrivingStylesManagement);
    }

    clearSelectedMetricMapping(): void {
        this.selectedMetricMapping = null;
        this.originalSelectedMetricMapping = null;
    }

    removeSelectedMetricMapping(): void {
        const indexOfSelectedMetricMapping = this.dirtyDrivingStyle.DrivingStyleMetricMappings.findIndex(mapping => mapping === this.selectedMetricMapping);

        if (indexOfSelectedMetricMapping === -1) {
            this.clearSelectedMetricMapping();
            return;
        }

        this.dirtyDrivingStyle.DrivingStyleMetricMappings.splice(indexOfSelectedMetricMapping, 1);
        this.clearSelectedMetricMapping();
    }

    resetSelectedMetricMapping(): void {
        const indexOfSelectedMetricMapping = this.dirtyDrivingStyle.DrivingStyleMetricMappings.findIndex(mapping => mapping === this.selectedMetricMapping);

        if (indexOfSelectedMetricMapping === -1) {
            this.clearSelectedMetricMapping();
            return;
        }

        this.dirtyDrivingStyle.DrivingStyleMetricMappings.splice(indexOfSelectedMetricMapping, 1);
        this.dirtyDrivingStyle.DrivingStyleMetricMappings.push(this.originalSelectedMetricMapping);

        this.clearSelectedMetricMapping();
    }

    onMetricMappingClicked(metricMapping: IDrivingStyleMetricMapping): void {
        const matchingAvailableMetricMapping = this.availableDrivingStyleMetricMappings.find(availableMetricMapping => availableMetricMapping.DrivingStyleMetric === metricMapping.DrivingStyleMetric);

        if (matchingAvailableMetricMapping) {
            matchingAvailableMetricMapping.Weighting = metricMapping.Weighting;
        }

        this.selectMetricMappingFromMetric(metricMapping.DrivingStyleMetric);
        this.drivingStylesService.updateCustomWeightingsError(this.dirtyDrivingStyle);
    }

    onMetricMappingChanged(metricMapping: IDrivingStyleMetricMapping): void {
        if (this.dirtyDrivingStyle.WeightingType === DrivingStyleWeightingType.Even) {
            return;
        }

        this.queryDrivingStylePerformanceSummary();
        this.drivingStylesService.updateCustomWeightingsError(this.dirtyDrivingStyle);
    }

    onMetricMappingsChanged(updatedMetricMappings: IDrivingStyleMetricMapping[]): void {
        this.drivingStylesService.updateCustomWeightingsError(this.dirtyDrivingStyle);
        this.queryDrivingStylePerformanceSummary();
    }

    onSliderValuesChanged(values: number[]): void {
        this.dirtyDrivingStyle.LowerRagBound = Math.round(values[0]);
        this.dirtyDrivingStyle.UpperRagBound = Math.round(values[1]);
    }

    onRagSliderDragEnd(): void {
        this.upperRagBoundControl.setValue(this.dirtyDrivingStyle.UpperRagBound);
        this.lowerRagBoundControl.setValue(this.dirtyDrivingStyle.LowerRagBound);

        this.upperRagBoundControl.updateValueAndValidity();
        this.lowerRagBoundControl.updateValueAndValidity();
    }

    onAssociationsValidation(valid: boolean): void {
        this.associationsValid = valid;
    }

    onAlterativeMetricClick(metric: DrivingStyleMetric): void {
        this.selectMetricMappingFromMetric(metric);
    }

    onWeightingTypeChange(): void {

        this.fixWeightingProperties();
        this.updateWeightingLevelDetails(this.dirtyDrivingStyle);
        this.drivingStylesService.updateCustomWeightingsError(this.dirtyDrivingStyle);
        this.queryDrivingStylePerformanceSummary();
    }

    onDrivingStyleTypeChanged(): void {
        this.selectedType = this.selectedType === DrivingStyleType.Efficiency
            ? DrivingStyleType.Safety
            : DrivingStyleType.Efficiency;

        this.dirtyDrivingStyle.DrivingStyleMetricMappings = [];
        this.setType();
        
        const nameControl = this.drivingStyleForm.get('Name');               
        nameControl.updateValueAndValidity();        
        
        this.setupAvailableDrivingStyleMetricMappings();
    }

    onTypeSelectionChanged(selectedButtons: IRadioButton): void {        
        this.selectedType = selectedButtons.value;
        this.dirtyDrivingStyle.DrivingStyleMetricMappings = [];
        this.setType();
        
        const nameControl = this.drivingStyleForm.get('Name');               
        nameControl.updateValueAndValidity();        
        
        this.setupAvailableDrivingStyleMetricMappings();
    }

    private updateWeightingLevelDetails(drivingStyle: IDrivingStyle): void {
        if (!drivingStyle?.DrivingStyleMetricMappings?.length) {
            return;
        }

        drivingStyle.DrivingStyleMetricMappings.forEach(metricMapping => this.drivingStylesService.addWeightingLevelDetailsToMetric(metricMapping, this.dirtyDrivingStyle.WeightingType));

        this.setupAvailableDrivingStyleMetricMappings();
    }

    //--

    private async getData(): Promise<void> {
        this.loading = true;

        const promises: Promise<any>[] = [
            this.drivingStylesService.getDefaultDrivingStyles(),
            this.drivingStylesService.getDrivingStyles(true),
            this.drivingStylesService.getDrivingStyleMetricDescriptionDetails()
        ];

        await Promise.all(promises).then(([defaultDrivingStyles, drivingStyles, drivingStyleMetricDescriptionDetails]) => {
            this.allDefaultDrivingStyles = defaultDrivingStyles;

            this.determineDrivingStyleType(defaultDrivingStyles, drivingStyles);
            this.createRadioButtons();

            this.drivingStyles = drivingStyles;
            this.drivingStyleMetricDescriptionDetails = drivingStyleMetricDescriptionDetails;
            this.metricMappingWeightingLevelDetails = this.drivingStylesService.getMetricMappingWeightingLevelDetails();

            this.setupAvailableDrivingStyleMetricMappings();
            this.prepareModel();

        }).finally(() => {

            this.queryDrivingStylePerformanceSummary();            
            this.buildForm();
            this.cd.detectChanges();
            this.loading = false;
        })
    }

    private selectMetricMappingFromMetric(metric: DrivingStyleMetric): void {
        this.selectedMetricMapping = this.dirtyDrivingStyle.DrivingStyleMetricMappings.find(existingMetricMapping => existingMetricMapping.DrivingStyleMetric === metric);
        this.originalSelectedMetricMapping = copy(this.selectedMetricMapping);
    }

    private async queryDrivingStylePerformanceSummary(): Promise<void> {

        if (this.dirtyDrivingStyle.WeightingType === DrivingStyleWeightingType.Even) {
            return Promise.resolve();
        }

        const calculatedDrivingStyle: IDrivingPerformanceSummary = await this.drivingStylesService.calculateDrivingScore(this.dirtyDrivingStyle, true);

        this.clearPerformanceSummaryOnMetricMappings(this.dirtyDrivingStyle);
        this.assignPerformanceSummaryOnMetricMappings(this.dirtyDrivingStyle, calculatedDrivingStyle);

        if (!this.selectedMetricMapping || !this.drivingStyleMetricEditor) {
            return;
        }

        this.drivingStyleMetricEditor.updateDisplayMetricMappings();
    }

    private clearPerformanceSummaryOnMetricMappings(drivingStyle: IDrivingStyle): void {

        if (!drivingStyle?.DrivingStyleMetricMappings?.length) {
            return;
        }

        drivingStyle.DrivingStyleMetricMappings.forEach(metricMapping => {
            metricMapping.metricMetadataItem = null;
        });
    }

    private assignPerformanceSummaryOnMetricMappings(drivingStyle: IDrivingStyle, calculatedDrivingStyle: IDrivingPerformanceSummary): void {

        if (!calculatedDrivingStyle?.CalculationResult?.DrivingStyleMetricScoreMetadataItems?.length) {
            return;
        }

        drivingStyle.DrivingStyleMetricMappings.forEach(metricMapping => {
            const metadataItem: IDrivingStyleMetricMetadataItem = calculatedDrivingStyle.CalculationResult.DrivingStyleMetricScoreMetadataItems.find(item => item.DrivingStyleMetric === metricMapping.DrivingStyleMetric);

            if (!metadataItem) {
                console.warn(`No metadataItem found for: ${metricMapping.DrivingStyleMetric} / ${DrivingStyleMetric[metricMapping.DrivingStyleMetric]}`);
                return;
            }

            metricMapping.metricMetadataItem = metadataItem;
        });
    }

    private prepareModel(): void {

        this.defaultDrivingStyle = this.allDefaultDrivingStyles.find(d => d.DrivingStyleType == this.selectedType);

        const routeParamDrivingStyleId = this.$stateParams["drivingStyleId"];

        if (routeParamDrivingStyleId) {
            this.drivingStyle = this.drivingStyles.find(existingDrivingStyle => existingDrivingStyle.Id === +this.drivingStyleId);
        }

        if (!this.drivingStyle) {
            this.drivingStyle = this.drivingStylesService.createDefaultDrivingStyle();
            this.drivingStyle.UpperRagBound = (this.defaultDrivingStyle.UpperRagBound > 0) ? this.defaultDrivingStyle.UpperRagBound : 70;
            this.drivingStyle.LowerRagBound = (this.defaultDrivingStyle.LowerRagBound > 0) ? this.defaultDrivingStyle.LowerRagBound : 30;
        }

        this.drivingStylesService.addDrivingStyleMetricDescriptionDetails(this.drivingStyle.DrivingStyleMetricMappings, this.drivingStyleMetricDescriptionDetails);
        this.drivingStylesService.addDrivingStyleMetricDefaults(this.drivingStyle.DrivingStyleMetricMappings, this.defaultDrivingStyle);
        this.drivingStylesService.addWeightingLevelDetailsToMetricMappings(this.drivingStyle.DrivingStyleMetricMappings);

        this.dirtyDrivingStyle = copy(this.drivingStyle);

        if (this.drivingStyle.DrivingStyleAssociations) {
            this.dirtyDrivingStyle.DrivingStyleAssociations = [...this.drivingStyle.DrivingStyleAssociations];
        }

        if (this.drivingStyle.DrivingStyleMetricMappings) {
            this.dirtyDrivingStyle.DrivingStyleMetricMappings = [...this.drivingStyle.DrivingStyleMetricMappings];
        }

        this.fixWeightingProperties();
        this.updateWeightingLevelDetails(this.dirtyDrivingStyle);
        this.queryDrivingStylePerformanceSummary();
    }

    private applyRouterDrivingStyleId(): void {
        const routeParamDrivingStyleId = this.$stateParams["drivingStyleId"];

        if (!routeParamDrivingStyleId) {
            return;
        }

        this.drivingStyleId = routeParamDrivingStyleId;
    }

    private setupAvailableDrivingStyleMetricMappings(): IDrivingStyleMetricMapping[] {
        this.availableDrivingStyleMetricMappings = [];

        if (!this.drivingStyleMetricDescriptionDetails?.length) {
            return;
        }

        this.drivingStyleMetricDescriptionDetails.forEach(description => {
            const newMetricMapping = this.drivingStylesService.createDrivingStyleMetricMapping(description.DrivingStyleMetric);

            if (newMetricMapping.DrivingStyleMetric === DrivingStyleMetric.Total) {
                return;
            }

            if (newMetricMapping.DrivingStyleMetric === DrivingStyleMetric.OverTorqueDuration) {
                return;
            }

            if (newMetricMapping.Type === this.selectedType || newMetricMapping.Type === DrivingStyleType.All) {
                this.availableDrivingStyleMetricMappings.push(newMetricMapping);
            }
        });

        this.defaultDrivingStyle = this.allDefaultDrivingStyles.find(d => d.DrivingStyleType == this.selectedType);

        this.drivingStylesService.addDrivingStyleMetricDescriptionDetails(this.availableDrivingStyleMetricMappings, this.drivingStyleMetricDescriptionDetails);
        this.drivingStylesService.addDrivingStyleMetricDefaults(this.availableDrivingStyleMetricMappings, this.defaultDrivingStyle);
    }

    private buildForm(): void {
        this.drivingStyleForm = this.formBuilder.group({
            Name: [this.dirtyDrivingStyle.Name ?? "", [
                Validators.required,
                Validators.minLength(3),
                Validators.maxLength(100),
                CustomFormValidators.emptyWhiteSpace,
                this.drivingStylesService.uniqueDrivingStyleNameValidator(this.dirtyDrivingStyle, this.drivingStyles)]
            ],
            UpperRagBound: [this.dirtyDrivingStyle.UpperRagBound, [Validators.required, Validators.min(0), Validators.max(100), this.validateRagBound()]],
            LowerRagBound: [this.dirtyDrivingStyle.LowerRagBound, [Validators.required, Validators.min(0), Validators.max(100), this.validateRagBound()]],
            WeightingType: [this.dirtyDrivingStyle.WeightingType ?? DrivingStyleWeightingType.Even, [Validators.required, Validators.min(0), Validators.max(2)]]
        });
    }

    private validateRagBound(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {

            if (!this.drivingStyleForm) {
                return null;
            }

            const upperRagBoundValue: number = this.upperRagBoundControl?.value;
            const lowerRagBoundValue: number = this.lowerRagBoundControl?.value;

            if (upperRagBoundValue <= lowerRagBoundValue) {
                return { customMessage: this.translationService.t("DrivingStyleEditor.UpperRagBoundValidationFail") }
            }

            return null;
        };
    }

    private fixWeightingProperties(): void {
        if (!this.dirtyDrivingStyle?.DrivingStyleMetricMappings?.length) {
            return;
        }

        const defaultMetricMappingPriorityOptions = this.metricMappingWeightingLevelDetails.find(option => option.isDefault);

        this.dirtyDrivingStyle.DrivingStyleMetricMappings.forEach(metricMapping => {
            const matchingMetricMappingPriorityOptions = this.metricMappingWeightingLevelDetails.find(detail => detail.value === metricMapping.Weighting);

            switch (this.dirtyDrivingStyle.WeightingType) {
                case DrivingStyleWeightingType.Even:
                    metricMapping.Weighting = null;
                    break;

                case DrivingStyleWeightingType.Simple:
                    metricMapping.Weighting = (matchingMetricMappingPriorityOptions && matchingMetricMappingPriorityOptions.level !== MetricMappingWeightingLevel.Custom)
                        ? matchingMetricMappingPriorityOptions.value
                        : defaultMetricMappingPriorityOptions.value;
                    break;

                case DrivingStyleWeightingType.Custom:
                    metricMapping.Weighting = (!!metricMapping.Weighting) ? metricMapping.Weighting : defaultMetricMappingPriorityOptions.value;
                    break;
            }
        });
    }

    private setStyleIdForMetricMappings(drivingStyle: IDrivingStyle): void {

        if (!drivingStyle?.DrivingStyleMetricMappings?.length) {
            return;
        }

        this.dirtyDrivingStyle.DrivingStyleMetricMappings?.forEach(mapping => {
            mapping.DrivingStyleId = this.dirtyDrivingStyle.Id;
        });
    }

    private fixAssociatons(drivingStyle: IDrivingStyle): void {
        if (!drivingStyle?.DrivingStyleAssociations?.length) {
            return;
        }

        this.drivingStyle.DrivingStyleAssociations.forEach(association => {
            association.VehicleType = (association.VehicleType === -1) ? null : association.VehicleType;
        })
    }

    private setType(): void {
        this.dirtyDrivingStyle.DrivingStyleType = this.selectedType;
    }

    private determineDrivingStyleType(defaults: IDrivingStyle[], styles: IDrivingStyle[]): void {        
        const isNew = this.drivingStyleId === -1;

        this.selectedType = isNew
            ? DrivingStyleType.Efficiency
            : styles.find(style => style.Id == this.drivingStyleId)?.DrivingStyleType ?? DrivingStyleType.Efficiency;

        this.defaultDrivingStyle = defaults.find(style => style.DrivingStyleType === this.selectedType);
    }

    private createRadioButtons(): void {
        this.allDefaultDrivingStyles.forEach(style => {
            this.radioButtons.push({
                label: this.translationService.translateEnum(DrivingStyleType, "DrivingStyleType", style.DrivingStyleType),                
                value: style.DrivingStyleType,
                disabled: this.drivingStyleId !== -1,
                description: this.translationService.translateEnum(DrivingStyleTypeDescription, "DrivingStyleTypeDescription", style.DrivingStyleType),    
                highlightColor: "#209058" //$green-color:             
            });
        });

        const index = this.radioButtons.findIndex( x=> x.value == this.defaultDrivingStyle.DrivingStyleType);
        
        this.selectedRadioButton = this.radioButtons[index];        
    }
}
